import {
  LayoutDashboard, Building2, Users, CalendarDays, Shield,
  Bell, Settings, Home, ClipboardList, FileCheck, AlertTriangle,
  BarChart3, UserCog, CreditCard, Wrench, MessageSquare, Inbox,
  UserPlus, Gavel,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import type { UserRole } from "@/types/database";
import { useUnreadCount } from "@/hooks/useNotifications";
import { useUnreadMessageCount } from "@/hooks/useMessages";
import { useMyAssignments } from "@/hooks/useUserAssignments";
import { usePmPermissions, type PmPermissions } from "@/hooks/usePmPermissions";
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent,
  SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem,
  SidebarHeader, SidebarFooter, SidebarSeparator,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";

interface NavItem {
  title: string;
  url: string;
  icon: React.ElementType;
  badgeKey?: string;
  roles: UserRole[];
  permKey?: keyof PmPermissions;
}

const navItems: NavItem[] = [
  { title: "Dashboard",      url: "/dashboard",            icon: LayoutDashboard, roles: ["strata_admin", "property_manager", "system_admin"] },
  { title: "My Dashboard",   url: "/individual-dashboard", icon: Home,            roles: ["individual"] },
  { title: "Host Dashboard", url: "/host-dashboard",       icon: Home,            roles: ["host"] },
  { title: "Units",          url: "/units",                icon: Building2,       roles: ["strata_admin", "property_manager", "system_admin"], permKey: "manage_units" },
  { title: "My Properties",  url: "/units",                icon: Building2,       roles: ["individual"] },
  { title: "Hosts",          url: "/hosts",                icon: Users,           roles: ["strata_admin", "property_manager", "system_admin"], permKey: "manage_hosts" },
  { title: "Bookings",       url: "/bookings",             icon: CalendarDays,    roles: ["strata_admin", "property_manager", "system_admin"], permKey: "manage_bookings" },
  { title: "My Bookings",    url: "/my-bookings",          icon: CalendarDays,    roles: ["host"] },
  { title: "Bookings",       url: "/bookings",             icon: CalendarDays,    roles: ["individual"] },
  { title: "Compliance",     url: "/compliance",           icon: Shield,          roles: ["strata_admin", "property_manager", "system_admin"], permKey: "manage_compliance" },
  { title: "My Documents",   url: "/my-documents",         icon: FileCheck,       roles: ["host"] },
  { title: "Documents",      url: "/my-documents",         icon: FileCheck,       roles: ["individual"] },
];

const adminItems: NavItem[] = [
  { title: "Team",           url: "/team",              icon: UserCog,       roles: ["strata_admin", "system_admin"] },
  { title: "Strata Config",  url: "/strata-config",     icon: Settings,      roles: ["strata_admin", "system_admin"] },
  { title: "Audit Log",      url: "/audit-log",         icon: ClipboardList, roles: ["strata_admin", "property_manager", "system_admin"], permKey: "view_audit_log" },
  { title: "Reports",        url: "/reports",           icon: BarChart3,     roles: ["strata_admin", "property_manager", "system_admin"], permKey: "view_reports" },
  { title: "Disputes",       url: "/disputes",          icon: AlertTriangle, roles: ["strata_admin", "property_manager", "host", "individual", "system_admin"], permKey: "manage_disputes" },
  { title: "Fines",          url: "/my-fines",          icon: Gavel,         roles: ["strata_admin", "property_manager", "host", "individual", "system_admin"], permKey: "manage_fines" },
  { title: "Amenities",         url: "/amenities",           icon: CalendarDays, roles: ["strata_admin", "property_manager", "host", "individual", "system_admin"] },
  { title: "Amenity Management",url: "/amenity-management",  icon: Settings,     roles: ["strata_admin", "property_manager", "host", "system_admin"], permKey: "manage_amenities" },
  { title: "Requests",       url: "/requests",          icon: Inbox,         roles: ["strata_admin", "property_manager", "host", "system_admin"] },
  { title: "Maintenance",    url: "/maintenance",       icon: Wrench,        roles: ["strata_admin", "property_manager", "host", "individual", "system_admin"], permKey: "manage_maintenance" },
  { title: "Messages",       url: "/messages",          icon: MessageSquare, badgeKey: "messages",      roles: ["strata_admin", "property_manager", "host", "individual", "system_admin"] },
  { title: "Notifications",  url: "/notifications",     icon: Bell,          badgeKey: "notifications", roles: ["strata_admin", "property_manager", "host", "individual", "system_admin"] },
  { title: "My Invitations", url: "/my-invitations",    icon: UserPlus,      roles: ["strata_admin", "property_manager", "host", "individual", "system_admin"] },
  { title: "Billing",        url: "/account/billing",   icon: CreditCard,    roles: ["strata_admin", "property_manager", "individual"] },
];

const END_ROUTES = new Set(["/dashboard", "/host-dashboard", "/individual-dashboard"]);

export function AppSidebar() {
  const { user } = useAuth();
  const location = useLocation();
  const { data: unreadCount = 0 } = useUnreadCount();
  const { data: assignments = [] } = useMyAssignments();
  const orgId = assignments[0]?.org_id ?? null;
  const { data: unreadMessages = 0 } = useUnreadMessageCount(orgId);
  const { data: pmPermissions } = usePmPermissions();
  const role = user?.role ?? "host";

  const isActive = (url: string) => {
    if (END_ROUTES.has(url)) return location.pathname === url;
    return location.pathname === url || location.pathname.startsWith(url + "/");
  };

  const filterByRole = (items: NavItem[]) =>
    items.filter((i) => {
      if (!i.roles.includes(role)) return false;
      if (role === "property_manager" && i.permKey) {
        return pmPermissions?.[i.permKey] !== false;
      }
      return true;
    });

  const renderItem = (item: NavItem) => {
    const active = isActive(item.url);
    return (
      <SidebarMenuItem key={item.title + item.url}>
        <SidebarMenuButton asChild>
          <Link
            to={item.url}
            className={cn(
              "relative",
              active ? "text-sidebar-accent-foreground font-medium" : "hover:bg-sidebar-accent/50"
            )}
          >
            {active && (
              <motion.div
                layoutId="sidebar-pill"
                className="absolute inset-0 rounded-md bg-sidebar-accent"
                transition={{ type: "spring", stiffness: 380, damping: 35 }}
              />
            )}
            <item.icon className="relative z-10 h-4 w-4 shrink-0" />
            <span className="relative z-10 flex-1">{item.title}</span>
            {item.badgeKey === "messages" && unreadMessages > 0 && (
              <Badge variant="destructive" className="relative z-10 ml-auto h-5 min-w-[20px] justify-center rounded-full px-1.5 text-xs">
                {unreadMessages}
              </Badge>
            )}
            {item.badgeKey === "notifications" && unreadCount > 0 && (
              <Badge variant="destructive" className="relative z-10 ml-auto h-5 min-w-[20px] justify-center rounded-full px-1.5 text-xs">
                {unreadCount}
              </Badge>
            )}
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar>
      <SidebarHeader className="p-4">
        <div className="flex items-center justify-center">
          <Link to="/dashboard" className="group" aria-label="Go to dashboard">
            <div className="rounded-2xl bg-gradient-to-br from-sidebar-primary/25 to-sidebar-primary/5 p-2 shadow-lg ring-1 ring-sidebar-primary/30 group-hover:ring-sidebar-primary/60 transition-all">
              <img src="/logo.png" alt="Stratakey" className="h-10 w-auto object-contain rounded-xl drop-shadow-sm group-hover:scale-105 transition-transform" />
            </div>
          </Link>
        </div>
      </SidebarHeader>

      <SidebarSeparator />

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {filterByRole(navItems).map(renderItem)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator />

        <SidebarGroup>
          <SidebarGroupLabel>Administration</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {filterByRole(adminItems).map(renderItem)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-3">
        <div className="flex items-center gap-2 rounded-md bg-sidebar-accent/50 p-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-sidebar-primary text-[11px] font-medium text-sidebar-primary-foreground">
            {user?.fullName?.split(" ").map((n) => n[0]).join("") ?? "?"}
          </div>
          <div className="flex flex-col text-xs min-w-0">
            <span className="font-medium text-sidebar-foreground truncate">{user?.fullName}</span>
            <span className="text-sidebar-foreground/60 capitalize truncate">{role.replace("_", " ")}</span>
          </div>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
